import React from 'react';
import { useIMask } from 'react-imask';
import cn from '../Utils/cn';

type Props = {
	id?: string;
	name?: string;
	type?: React.ComponentPropsWithoutRef<'input'>['type'];
	autoComplete?: string;
	lines?: number;
	state?: string;
	placeholder?: string;
	label?: string;
	value?: string | number;
	disabled?: boolean;
	hasLeftIcon?: boolean;
	maskOpts?: IMask.AnyMaskedOptions;
	className?: string;
	setValue?: (value: string) => void;
};

type InputProps = {
	multiline?: false;
} & Props &
	React.ComponentPropsWithoutRef<'input'>;

type TextAreaProps = {
	multiline: true;
} & Props &
	React.ComponentPropsWithoutRef<'textarea'>;

const DefaultInput: React.FC<InputProps | TextAreaProps> = ({
	id,
	name = id,
	type = 'type',
	autoComplete,
	multiline = false,
	lines = 3,
	placeholder = '',
	value = '',
	state,
	disabled = false,
	hasLeftIcon = false,
	maskOpts,
	className,
	setValue = () => {},
	...props
}) => {
	const { ref, maskRef } = useIMask(maskOpts || { mask: '' });

	React.useEffect(() => {
		if (value != null && typeof value !== 'string') {
			setValue(value.toString());
		}

		if (typeof value === 'string' && maskRef.current && value !== maskRef.current.value) {
			maskRef.current.value = value;
			maskRef.current.updateValue();
		}
	}, [value, maskRef, setValue]);

	const classes = React.useMemo(
		() =>
			cn(
				'w-full w-[270px] px-[1.6rem] rounded-2xl border border-solid border-base-grey-5 text-base-grey-5 bg-transparent font-roboto text-lg leading-none bg-white text-base-grey-5 placeholder:text-base-grey-5 placeholder:opacity-60',
				{
					'py-[1.6rem] hover:p-6 focus:p-6 data-[error=true]:p-6': multiline,
					'h-20 hover:px-6 focus:px-6 data-[error=true]:px-6': !multiline,
					'pl-[51px] hover:pl-[50px] focus:pl-[50px] data-[error=true]:pl-[50px]': hasLeftIcon,
					'border-2 border-error-dark bg-error-light': state === 'error',
				},
				'hover:border-2 hover:border-brand-main',
				'focus:border-2 focus:border-brand-main focus:bg-brand-light focus:outline-none',
				className,
			),
		[className, hasLeftIcon, multiline, state],
	);

	if (multiline) {
		return (
			<textarea
				{...(props as TextAreaProps)}
				ref={ref as any}
				id={id}
				name={name}
				rows={lines}
				autoComplete={autoComplete}
				disabled={disabled}
				onInput={maskRef.current && (() => setValue(maskRef.current.value))}
				placeholder={placeholder}
				data-error={state === 'error' && 'true'}
				value={maskRef.current ? maskRef.current.value : value}
				className={classes}
			/>
		);
	}

	return (
		<input
			{...(props as InputProps)}
			ref={ref as any}
			type={type}
			id={id}
			name={name}
			autoComplete={autoComplete}
			disabled={disabled}
			onInput={maskRef.current && (() => setValue(maskRef.current.value))}
			placeholder={placeholder}
			data-error={state === 'error' && 'true'}
			value={maskRef.current ? maskRef.current.value : value}
			className={classes}
		/>
	);
};

export default DefaultInput;
